import { Component, inject, OnDestroy, OnInit } from '@angular/core';
import { ColorConfig, OutputModelColors, ThreedViewerComponent, ThreedViewerMode } from 'app/components/threed-viewer';
import { DynamicDialogConfig, DynamicDialogRef } from 'primeng/dynamicdialog';
import { ReviewOrderFilesModalData } from './review-order-files-modal-data';
import { CommonModule } from '@angular/common';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { Button } from 'primeng/button';
import { FileRenderer } from 'app/core/enums';
import { finalize, forkJoin, map } from 'rxjs';
import { TreatmentApiClient } from 'app/core/api-clients';
import { OrderFileDownloadService } from 'app/core/services';
import { FileDownload } from 'app/core/models';
import { TranslateValuePipe } from 'app/shared/pipes/translate-value.pipe';
import { SafePipe } from 'safe-pipe';
import { SpinnerComponent } from 'app/components/common/spinner';

interface DataChunkItem {
  label: string;
  value: string;
}

@Component({
  selector: 'wl-review-order-files-modal',
  standalone: true,
  imports: [CommonModule, ThreedViewerComponent, TranslatePipe, Button, TranslateValuePipe, SafePipe, SpinnerComponent],
  templateUrl: './review-order-files-modal.component.html',
  styleUrl: './review-order-files-modal.component.scss',
})
export class ReviewOrderFilesModalComponent implements OnInit, OnDestroy {
  // MODIFIED: Implement OnDestroy
  private readonly _config = inject<DynamicDialogConfig<ReviewOrderFilesModalData>>(DynamicDialogConfig);
  private readonly _ref = inject(DynamicDialogRef);
  private readonly _treatmentClient = inject(TreatmentApiClient);
  private readonly _downloadService = inject(OrderFileDownloadService);
  private readonly _translateService = inject(TranslateService);

  protected readonly ThreedViewerMode = ThreedViewerMode;

  protected linksDictionary: Record<string, string> = {};
  protected textContentDictionary: Record<string, string> = {};
  protected data?: ReviewOrderFilesModalData | null;
  protected colorConfig?: ColorConfig | null;
  protected advanceDataChunks: DataChunkItem[] = [];
  protected disabled = false;
  protected isApproving = false;
  protected isImageLoading = false;
  protected isImageLoaded = false;
  protected isImageLoadFailed = false;

  ngOnInit() {
    this.data = this._config.data;
    if (!this.data) return;

    const { productType } = this.data;
    this.colorConfig = OutputModelColors.find(i => i.treatmentType === productType);
    this.parseDataChunks();

    if (this.data.renderer !== FileRenderer.threedViewer) {
      if (this.data.renderer === FileRenderer.image) {
        this.isImageLoading = true;
      }

      this.loadPreviewFiles();
    }
  }

  ngOnDestroy(): void {
    Object.values(this.linksDictionary)
      .filter(url => url.startsWith('blob:'))
      .forEach(url => URL.revokeObjectURL(url));
  }

  private loadPreviewFiles() {
    if (!this.data?.files?.length) {
      return;
    }

    if (this.data.renderer === FileRenderer.sdsplink) {
      this.loadSdsplinkPreviewFiles();
      return;
    }

    this._treatmentClient
      .getDownloadLinks(this.data.files.map(file => ({ ...file, fileId: file.id, fileExtension: file.extension })))
      .pipe(
        map(links => links.reduce<Record<string, string>>((acc, link) => ({ ...acc, [link.fileId]: link.url }), {})),
        finalize(() => {
          if (this.data?.renderer === FileRenderer.image) {
            this.isImageLoading = false;
          }
        })
      )
      .subscribe({
        next: dictionary => {
          if (this.data?.renderer === FileRenderer.image) {
            const previewFileId = this.data.files[0]?.id;
            this.isImageLoaded = false;
            this.isImageLoadFailed = previewFileId == null || dictionary[previewFileId] == null;
          }

          this.linksDictionary = dictionary;
        },
        error: () => {
          if (this.data?.renderer === FileRenderer.image) {
            this.isImageLoadFailed = true;
          }
        },
      });
  }

  protected onImageLoaded() {
    this.isImageLoaded = true;
    this.isImageLoadFailed = false;
  }

  protected onImageLoadError() {
    this.isImageLoaded = false;
    this.isImageLoadFailed = true;
  }

  private loadSdsplinkPreviewFiles() {
    const filesToDownload: FileDownload[] = this.data!.files.map(f => ({
      id: f.id,
      name: f.name,
      extension: f.extension,
      inProgress: false,
      progress: 0,
      blob: undefined,
    }));

    forkJoin(filesToDownload.map(file => this._downloadService.download(file))).subscribe(async downloadedFiles => {
      const textContentDictionary: Record<string, string> = {};

      for (const file of downloadedFiles) {
        if (file.blob) {
          textContentDictionary[file.id] = await file.blob.text();
        }
      }

      this.textContentDictionary = textContentDictionary;
    });
  }

  private parseDataChunks() {
    const {
      _config: { data },
    } = this;

    const chunks: DataChunkItem[] = [];
    if (data != null) {
      const { order } = data;

      chunks.push({
        label: 'general.product',
        value: order.treatmentType,
      });

      if (!this.data?.hideSubType) {
        chunks.push({
          label: 'general.treatmentSubType',
          value: order.treatmentSubType,
        });
      }

      if ((order.teethNotes && order.teethNotes.length > 0) || this.data?.selectedTeeth) {
        const selectedTeeth = this.data?.selectedTeeth;
        chunks.push({
          label: 'general.selectedTeeth',
          value: selectedTeeth
            ? [...selectedTeeth].sort((a, b) => Number(a) - Number(b)).join(',')
            : [...order.teethNotes]
                .sort((a, b) => a.teeth - b.teeth)
                .reduce((acc, c) => `${acc}${acc ? ', ' : ''}${c.teeth}`, ''),
        });
      }
    }

    this.advanceDataChunks = chunks;
  }

  protected async onApprove() {
    if (this.disabled) return;

    this.isApproving = true;
    this.disabled = true;
    try {
      await (this.data?.approveFn?.() ?? Promise.resolve(false));
    } catch (e) {
      console.warn(`Failed to approve\n${this.data}\n`, e);
    } finally {
      this.isApproving = false;
      this.disabled = false;
    }

    this.onClose();
  }

  protected async onRequestRevision() {
    if (this.disabled) return;

    this.disabled = true;
    await (this.data?.requestRevisionFn?.() ?? Promise.resolve(false));

    this.disabled = false;

    this.onClose();
  }

  protected onClose() {
    this._ref.close();
  }

  protected readonly FileRenderer = FileRenderer;
}
